import { HTTPInstance } from "@http/http-instance";
import { FeatureFlag } from "@http/constants";
import { payTipMock } from "./TipsAPI.mock";
import type {
  StandaloneTipPayload,
  StandaloneTipResult,
} from "./TipsAPI.model";

/**
 * A tip with no bill behind it.
 *
 * Its own endpoint rather than the payment one: `POST /payments/tables/:id`
 * refuses a table with no active order, and its custom method wants a positive
 * amount for the bill itself, so there is no way to spell "nothing for the
 * bill, this much for the waiter".
 *
 * `amount` is what the recipient receives. The venue's fee is added on top by
 * the backend, which is why the screen shows it as a separate line and never
 * folds it in here. See tipFeeGrosze in helpers/cartPricing.ts.
 */
const payTipReal = async (
  restaurantId: string,
  payload: StandaloneTipPayload,
): Promise<StandaloneTipResult> => {
  const { data } = await HTTPInstance.post<{
    tipId?: string;
    orderCode?: string | number;
  }>(`/public/restaurants/${restaurantId}/tips`, {
    tableId: payload.tableId,
    amount: payload.amountGrosze,
    userToTip: payload.userToTip,
  });

  return {
    tipId: data?.tipId ?? "",
    // Numeric on the wire, and it goes into a URL. See helpers/vivaCheckout.
    orderCode: String(data?.orderCode ?? ""),
    paidAmountGrosze: payload.amountGrosze,
  };
};

export const payTip = (
  restaurantId: string,
  payload: StandaloneTipPayload,
): Promise<StandaloneTipResult> =>
  FeatureFlag.USE_MOCK_TIPS
    ? payTipMock(payload)
    : payTipReal(restaurantId, payload);
